import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatCurrency, formatPreviewQuantity } from '../../utils/formatCurrency';

export const PREVIEW_LINE_COLLAPSE_THRESHOLD = 5;

export const previewBackLabelFromPath = (returnTo: string): string => {
  if (returnTo.includes('/view/bill/')) return 'Back to bill';
  if (returnTo.includes('/view/invoice/')) return 'Back to invoice';
  if (returnTo.includes('/view/payment/')) return 'Back to payment';
  if (returnTo.includes('/view/quotation/')) return 'Back to quotation';
  return 'Back';
};

export const previewActionClasses = {
  primary:
    'inline-flex items-center gap-2 px-3 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-700 disabled:opacity-50',
  success:
    'inline-flex items-center gap-2 px-3 py-2 bg-green-700 text-white rounded-lg text-sm hover:bg-green-800 disabled:opacity-50',
  secondary:
    'inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50',
  danger:
    'inline-flex items-center gap-2 px-3 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50',
  dangerStrong:
    'inline-flex items-center gap-2 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50',
  close: 'px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm ml-auto',
};

export const PreviewSkeleton = () => (
  <div className="space-y-5 animate-pulse">
    <div className="flex gap-2">
      <div className="h-5 w-16 rounded-full bg-gray-200" />
    </div>
    <div className="rounded-xl border border-gray-200 p-4 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="h-10 bg-gray-200 rounded" />
        <div className="h-10 bg-gray-200 rounded" />
      </div>
      <div className="h-2 bg-gray-100 rounded-full" />
      <div className="h-4 w-2/3 bg-gray-100 rounded" />
    </div>
    <div className="space-y-2">
      <div className="h-4 w-24 bg-gray-200 rounded" />
      <div className="h-10 bg-gray-100 rounded" />
      <div className="h-10 bg-gray-100 rounded" />
      <div className="h-10 bg-gray-100 rounded" />
    </div>
  </div>
);

export const PreviewSectionHeading = ({ children }: { children: ReactNode }) => (
  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{children}</h4>
);

type PreviewAmountProps = {
  label: string;
  amount: string;
  tone?: 'default' | 'owed' | 'paid';
};

const amountToneClass = (tone: PreviewAmountProps['tone']) => {
  switch (tone) {
    case 'owed':
      return 'text-red-600';
    case 'paid':
      return 'text-green-600';
    default:
      return 'text-gray-900';
  }
};

export const PreviewFinancialHero = ({
  amounts,
  progressPercent,
}: {
  amounts: PreviewAmountProps[];
  progressPercent?: number;
}) => (
  <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4">
    <div className={`grid gap-4 ${amounts.length > 2 ? 'grid-cols-3' : 'grid-cols-2'}`}>
      {amounts.map((item) => (
        <div key={item.label}>
          <span className="text-xs text-gray-500 block mb-0.5">{item.label}</span>
          <span className={`text-lg font-semibold tabular-nums ${amountToneClass(item.tone)}`}>
            {formatCurrency(item.amount)}
          </span>
        </div>
      ))}
    </div>
    {progressPercent != null && progressPercent > 0 && progressPercent < 100 && (
      <div className="mt-4">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Paid</span>
          <span>{Math.round(progressPercent)}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full rounded-full bg-green-500 transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
    )}
  </div>
);

export const PreviewMetaGrid = ({ children }: { children: ReactNode }) => (
  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">{children}</div>
);

export const PreviewMetaItem = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className={label === 'Customer' ? 'col-span-2' : undefined}>
    <span className="text-gray-500 block text-xs">{label}</span>
    <span className="text-gray-900">{children}</span>
  </div>
);

export type PreviewLinkedRow = {
  id: string | number;
  title: string;
  subtitle?: string;
  amount: string;
  onClick: () => void;
};

export const PreviewLinkedList = ({
  rows,
  footerLabel,
  footerAmount,
}: {
  rows: PreviewLinkedRow[];
  footerLabel?: string;
  footerAmount?: string;
}) => (
  <div className="rounded-lg border border-gray-200 overflow-hidden divide-y divide-gray-200">
    {rows.map((row) => (
      <button
        key={row.id}
        type="button"
        onClick={row.onClick}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50 transition-colors"
      >
        <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate text-slate-800">{row.title}</div>
          {row.subtitle ? <div className="text-xs text-gray-500 truncate">{row.subtitle}</div> : null}
        </div>
        <span className="font-mono shrink-0 text-gray-700 tabular-nums">
          {formatCurrency(row.amount)}
        </span>
      </button>
    ))}
    {footerLabel && footerAmount != null && (
      <div className="flex justify-between gap-3 px-3 py-2 bg-slate-50 text-sm font-medium text-gray-700">
        <span>{footerLabel}</span>
        <span className="font-mono tabular-nums">{formatCurrency(footerAmount)}</span>
      </div>
    )}
  </div>
);

export type PreviewLineItemRow = {
  id: number;
  name: string;
  sku?: string | null;
  qty: string;
  unitPrice: string;
  lineTotal: string;
};

export const PreviewCollapsibleLineItems = ({
  lines,
  collapseThreshold = PREVIEW_LINE_COLLAPSE_THRESHOLD,
}: {
  lines: PreviewLineItemRow[];
  collapseThreshold?: number;
}) => {
  const [expanded, setExpanded] = useState(lines.length <= collapseThreshold);
  const visibleLines = expanded ? lines : lines.slice(0, collapseThreshold);
  const hiddenCount = lines.length - collapseThreshold;

  if (lines.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <PreviewSectionHeading>Line items</PreviewSectionHeading>
        {lines.length > collapseThreshold && (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
            />
            {expanded ? 'Show less' : `Show all (${lines.length})`}
          </button>
        )}
      </div>
      <ul className="text-sm border border-gray-200 rounded-lg divide-y divide-gray-200 overflow-hidden">
        {visibleLines.map((line) => (
          <li key={line.id} className="flex justify-between gap-3 px-3 py-2 bg-white">
            <div className="min-w-0">
              <div className="font-medium truncate text-gray-900">{line.name}</div>
              {line.sku?.trim() && (
                <div className="text-xs text-gray-500 font-mono truncate">{line.sku}</div>
              )}
              <div className="text-xs text-gray-500 tabular-nums">
                {formatPreviewQuantity(line.qty)} × {formatCurrency(line.unitPrice)}
              </div>
            </div>
            <span className="font-mono shrink-0 text-gray-800 tabular-nums">
              {formatCurrency(line.lineTotal)}
            </span>
          </li>
        ))}
        {!expanded && hiddenCount > 0 && (
          <li className="px-3 py-2 text-xs text-gray-500 bg-gray-50">
            +{hiddenCount} more line{hiddenCount === 1 ? '' : 's'}
          </li>
        )}
      </ul>
    </div>
  );
};

export const PreviewActionFooter = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-wrap items-center gap-2">{children}</div>
);
